<template>
  <div>
    <div class="title">点击按钮后倒数拍照进行核验</div>
    <div class="videoContainer">
      <video
        ref="video"
        id="video"
        autoplay
        muted
        playsinline
        v-show="!showCaptured"
      ></video>
      <canvas ref="canvas" id="canvas" v-show="showCaptured"></canvas>
      <div class="videoOverlay">{{ statusText }}</div>
    </div>
    <div class="tipsText">
      请保持脸部在画面中央（环境光线强弱、反光等可能影响识别）。
    </div>
    <div class="inlineWarning" v-if="noFaceDetected">没有人脸，请重新拍照</div>
    <div class="buttons">
      <el-button
        type="primary"
        @click="handleStartCapture"
        :disabled="isTracking"
        v-if="!isVerifiedSuccess"
      >
        {{ hasCaptured ? "重新拍照" : "开始拍照" }}
      </el-button>
      <el-button
        type="success"
        @click="handleVerify"
        :disabled="!hasCaptured || isTracking || mustRecapture || isDetecting"
        v-if="!isVerifiedSuccess"
      >
        去认证核验
      </el-button>
    </div>
  </div>
</template>

<script>
require("tracking/build/tracking-min.js");
require("tracking/build/data/face-min.js");
export default {
  name: "VideoCanvas",
  props: {
    mediaConstraints: {
      type: Object,
      default: () => ({
        video: {
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 480 },
          facingMode: "user",
        },
        audio: false,
      }),
    },
    examinationId: {
      // 新增：考试ID，用于接口路径参数
      type: [String, Number],
      required: false,
      default: undefined,
    },
  },
  data() {
    return {
      width: 0,
      height: 0,
      TrackingTime: 3,
      TrackingInterval: null,
      isTracking: false,
      hasCaptured: false,
      capturedFile: null,
      isVerifiedSuccess: false,
      mustRecapture: false,
      noFaceDetected: false,
      isDetecting: false,
      showCaptured: false,
      // 新增：检测调试与合规标志
      rawFaceDetected: false,
      nonCompliantFace: false,
      localStream: null,
    };
  },
  computed: {
    statusText() {
      if (this.isVerifiedSuccess) return "认证成功，请进入考场";
      if (this.isTracking) return `倒计时 ${this.TrackingTime}`;
      if (this.isDetecting) return "正在检测人脸...";
      if (this.nonCompliantFace)
        return "检测到人脸，但不满足拍摄规范，请居中并靠近镜头";
      if (this.noFaceDetected) return "没有人脸";
      if (this.mustRecapture) return "认证失败，请重新拍照";
      if (this.hasCaptured) return "已完成拍照，请点击“去认证核验”或“重新拍照”";
      return "请点击下方“开始拍照”按钮，倒数后自动拍照";
    },
  },
  mounted() {
    this.setTime();
  },
  beforeUnmount() {
    this.onStopTracking();
  },
  methods: {
    handleStartCapture() {
      if (this.isTracking) return;
      this.isVerifiedSuccess = false;
      this.mustRecapture = false;
      this.noFaceDetected = false;
      this.nonCompliantFace = false;
      this.rawFaceDetected = false;
      this.hasCaptured = false;
      this.capturedFile = null;
      this.showCaptured = false; // 重新拍照时恢复显示视频
      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      this.TrackingTime = 3;
      this.isTracking = true;
      if (this.TrackingInterval) clearInterval(this.TrackingInterval);
      this.TrackingInterval = setInterval(() => {
        this.TrackingTime--;
        if (this.TrackingTime <= 0) {
          clearInterval(this.TrackingInterval);
          this.TrackingInterval = null;
          this.getPhoto();
          this.isTracking = false;
        }
      }, 1000);
    },
    async getPhoto() {
      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      const stream = video && video.srcObject;

      // Ensure the video frame is ready before capture
      try {
        if (video && video.readyState < 2) {
          await new Promise((r) => setTimeout(r, 200));
        }
      } catch (e) {}

      // 优先使用原生 ImageCapture.grabFrame()，更快地捕获当前帧
      if (
        stream &&
        "getVideoTracks" in stream &&
        typeof window.ImageCapture !== "undefined"
      ) {
        const tracks = stream.getVideoTracks();
        if (tracks && tracks.length) {
          try {
            const track = tracks[0];
            const imageCapture = new window.ImageCapture(track);
            const imageBitmap = await imageCapture.grabFrame();
            const vw = imageBitmap.width;
            const vh = imageBitmap.height;
            canvas.width = vw;
            canvas.height = vh;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(imageBitmap, 0, 0, vw, vh);
            this.showCaptured = true;
            this.hasCaptured = true;
            const blob = await new Promise((resolve) =>
              canvas.toBlob(resolve, "image/jpeg", 0.95)
            );
            const dataUri = await this.blobToDataURL(blob);
            this.capturedFile = this.blobToFile(blob, "capture.jpg");
            console.log(
              "[capture] (ImageCapture) size:",
              vw,
              vh,
              "dataURI len:",
              dataUri ? dataUri.length : 0
            );
            if (this.isCanvasBlank(canvas))
              console.warn(
                "[capture] (ImageCapture) canvas looks blank (very dark)."
              );

            // 检测：FaceDetector优先；若不可用再回退tracking.js（避免FD判定无脸时被tracking误判为有脸）
            this.isDetecting = true;
            this.noFaceDetected = false;
            Promise.resolve()
              .then(async () => {
                const [fdResult, tkResult] = await Promise.all([
                  this.detectWithFaceDetector(dataUri), // true/false/null
                  this.detectFaceDownscaled(dataUri), // true/false
                ]);
                let hasFace = fdResult === true || tkResult === true;
                if (!hasFace && !this.rawFaceDetected) {
                  const sensitive = await this.detectFaceFullsizeSensitive(
                    dataUri
                  );
                  hasFace = sensitive;
                }
                return hasFace;
              })
              .then((hasFaceFinal) => {
                if (!hasFaceFinal) {
                  if (this.rawFaceDetected) {
                    this.nonCompliantFace = true;
                    this.mustRecapture = true;
                    this.$message.warning(
                      "检测到人脸，但不满足拍摄规范，请居中并靠近镜头"
                    );
                  } else {
                    this.noFaceDetected = true;
                    this.mustRecapture = true;
                    this.$message.warning("没有人脸");
                  }
                }
              })
              .finally(() => {
                this.isDetecting = false;
              });
            return;
          } catch (e) {
            // fallback to canvas path
          }
        }
      }

      // 回退：使用画布从视频帧捕获并展示（异步导出，提升性能）
      const vw = video.videoWidth || video.getBoundingClientRect().width;
      const vh = video.videoHeight || video.getBoundingClientRect().height;
      canvas.width = vw;
      canvas.height = vh;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, vw, vh);
      this.showCaptured = true;
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.95)
      );
      const dataUri = await this.blobToDataURL(blob);
      const file = this.blobToFile(blob, "capture.jpg");
      this.capturedFile = file;
      this.hasCaptured = true;
      console.log(
        "[capture] (canvas) size:",
        vw,
        vh,
        "dataURI len:",
        dataUri ? dataUri.length : 0
      );
      if (this.isCanvasBlank(canvas))
        console.warn("[capture] (canvas) canvas looks blank (very dark).");

      // 检测：FaceDetector优先；若不可用再回退tracking.js（避免FD判定无脸时被tracking误判为有脸）
      this.isDetecting = true;
      this.noFaceDetected = false;
      Promise.resolve()
        .then(async () => {
          const [fdResult, tkResult] = await Promise.all([
            this.detectWithFaceDetector(dataUri),
            this.detectFaceDownscaled(dataUri),
          ]);
          let hasFace = fdResult === true || tkResult === true;
          if (!hasFace && !this.rawFaceDetected) {
            const sensitive = await this.detectFaceFullsizeSensitive(dataUri);
            hasFace = sensitive;
          }
          return hasFace;
        })
        .then((hasFaceFinal) => {
          if (!hasFaceFinal) {
            if (this.rawFaceDetected) {
              this.nonCompliantFace = true;
              this.mustRecapture = true;
              this.$message.warning(
                "检测到人脸，但不满足拍摄规范，请居中并靠近镜头"
              );
            } else {
              this.noFaceDetected = true;
              this.mustRecapture = true;
              this.$message.warning("没有人脸");
            }
          }
        })
        .finally(() => {
          this.isDetecting = false;
        });
    },
    // 在主画布上标注检测到的人脸框，便于可视化确认（srcW/srcH为检测源尺寸）
    drawRectsToMainCanvas(rects, srcW, srcH, color = "#00ff00") {
      try {
        const canvas = this.$refs.canvas;
        if (!canvas || !rects || !rects.length) return;
        const ctx = canvas.getContext("2d");
        const scaleX = canvas.width / srcW;
        const scaleY = canvas.height / srcH;
        ctx.save();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        rects.forEach((r) => {
          const x = (r.x || r.left || 0) * scaleX;
          const y = (r.y || r.top || 0) * scaleY;
          const w = (r.width || r.w || 0) * scaleX;
          const h = (r.height || r.h || 0) * scaleY;
          if (w > 0 && h > 0) ctx.strokeRect(x, y, w, h);
        });
        ctx.restore();
      } catch (e) {}
    },
    // 简单的空画布检测：取中心像素亮度，极暗视作空帧（用于调试）
    isCanvasBlank(canvas) {
      try {
        if (!canvas) return true;
        const w = canvas.width || 0;
        const h = canvas.height || 0;
        if (w === 0 || h === 0) return true;
        const ctx = canvas.getContext("2d");
        const data = ctx.getImageData(
          Math.floor(w / 2),
          Math.floor(h / 2),
          1,
          1
        ).data;
        const avg = (data[0] + data[1] + data[2]) / 3;
        return avg < 5; // near-black
      } catch (e) {
        return false;
      }
    },
    // 将图片缩放到较小尺寸后进行检测，tracking.js在较低分辨率下更稳定（增加严格过滤，降低误判）
    detectFaceDownscaled(dataUri) {
      return new Promise((resolve) => {
        try {
          const img = new Image();
          img.onload = () => {
            const maxW = 320;
            const scale = img.width > maxW ? maxW / img.width : 1;
            const dw = Math.floor(img.width * scale);
            const dh = Math.floor(img.height * scale);
            const off = document.createElement("canvas");
            off.width = dw;
            off.height = dh;
            const octx = off.getContext("2d");
            octx.imageSmoothingEnabled = true;
            octx.drawImage(img, 0, 0, dw, dh);
            const tracker = new tracking.ObjectTracker("face");
            tracker.setInitialScale(1);
            tracker.setStepSize(1);
            tracker.setEdgesDensity(0.12);
            const timer = setTimeout(() => resolve(false), 1800);
            tracker.on("track", (event) => {
              clearTimeout(timer);
              const rects = Array.isArray(event.data) ? event.data : [];
              this.rawFaceDetected = this.rawFaceDetected || rects.length > 0;
              const passed = rects.filter(
                (r) =>
                  this.isValidFaceRect(r, dw, dh) &&
                  this.rectHasTexture(octx, r)
              );
              if (passed.length)
                this.drawRectsToMainCanvas(passed, dw, dh, "#00ff00");
              console.log(
                "[tracking.js] faces:",
                rects.length,
                "passed:",
                passed.length
              );
              if (passed.length > 0) {
                resolve(true);
              } else {
                if (this.rawFaceDetected) this.nonCompliantFace = true;
                resolve(false);
              }
            });
            tracking.track(off, tracker);
          };
          img.onerror = () => resolve(false);
          img.src = dataUri;
        } catch (e) {
          resolve(false);
        }
      });
    },
    // 使用浏览器内置FaceDetector（如Chrome/Edge），更稳健；不可用或异常返回 null（避免误判时回退）
    detectWithFaceDetector(dataUri) {
      return new Promise((resolve) => {
        try {
          if (!("FaceDetector" in window)) return resolve(null);
          const detector = new window.FaceDetector({
            fastMode: true,
            maxDetectedFaces: 3,
          });
          const img = new Image();
          img.onload = () => {
            const maxW = 640;
            const scale = img.width > maxW ? maxW / img.width : 1;
            const dw = Math.floor(img.width * scale);
            const dh = Math.floor(img.height * scale);
            const off = document.createElement("canvas");
            off.width = dw;
            off.height = dh;
            const octx = off.getContext("2d");
            octx.drawImage(img, 0, 0, dw, dh);
            const timer = setTimeout(() => resolve(false), 1800);
            detector
              .detect(off)
              .then((faces) => {
                clearTimeout(timer);
                const rects = Array.isArray(faces)
                  ? faces.map((f) => f.boundingBox || f)
                  : [];
                this.rawFaceDetected = this.rawFaceDetected || rects.length > 0;
                const passed = rects.filter(
                  (r) =>
                    this.isValidFaceRect(r, dw, dh) &&
                    this.rectHasTexture(octx, r)
                );
                if (passed.length)
                  this.drawRectsToMainCanvas(passed, dw, dh, "#ffcc00");
                console.log(
                  "[FaceDetector] faces:",
                  rects.length,
                  "passed:",
                  passed.length
                );
                if (passed.length > 0) {
                  resolve(true);
                } else {
                  if (this.rawFaceDetected) this.nonCompliantFace = true;
                  resolve(false);
                }
              })
              .catch(() => {
                clearTimeout(timer);
                resolve(null);
              });
          };
          img.onerror = () => resolve(null);
          img.src = dataUri;
        } catch (e) {
          resolve(null);
        }
      });
    },
    // 更宽松的全尺寸备用检测：在前两次检测都未发现任何人脸时再尝试（不做纹理过滤，几何阈值放宽）
    detectFaceFullsizeSensitive(dataUri) {
      return new Promise((resolve) => {
        try {
          const img = new Image();
          img.onload = () => {
            const dw = img.width;
            const dh = img.height;
            const off = document.createElement("canvas");
            off.width = dw;
            off.height = dh;
            const octx = off.getContext("2d");
            octx.drawImage(img, 0, 0, dw, dh);
            const tracker = new tracking.ObjectTracker("face");
            tracker.setInitialScale(1);
            tracker.setStepSize(1);
            tracker.setEdgesDensity(0.15);
            const timer = setTimeout(() => resolve(false), 2000);
            tracker.on("track", (event) => {
              clearTimeout(timer);
              const rects = Array.isArray(event.data) ? event.data : [];
              this.rawFaceDetected = this.rawFaceDetected || rects.length > 0;
              const passed = rects.filter((r) =>
                this.isValidFaceRectRelaxed(r, dw, dh)
              );
              if (passed.length)
                this.drawRectsToMainCanvas(passed, dw, dh, "#3399ff");
              console.log(
                "[tracking.js sensitive] faces:",
                rects.length,
                "passed:",
                passed.length
              );
              resolve(passed.length > 0);
            });
            tracking.track(off, tracker);
          };
          img.onerror = () => resolve(false);
          img.src = dataUri;
        } catch (e) {
          resolve(false);
        }
      });
    },
    // 放宽的几何规则，用于备用检测（尽量不要过度严格，以便抓到远/偏的脸）
    isValidFaceRectRelaxed(rect, w, h) {
      if (!rect || !w || !h) return false;
      const x = Math.max(0, rect.x || rect.left || 0);
      const y = Math.max(0, rect.y || rect.top || 0);
      const rw = Math.max(0, rect.width || rect.w || 0);
      const rh = Math.max(0, rect.height || rect.h || 0);
      if (rw <= 0 || rh <= 0) return false;
      const ratio = rw / rh;
      if (ratio < 0.6 || ratio > 1.8) return false;
      const areaRatio = (rw * rh) / (w * h);
      if (areaRatio < 0.03 || areaRatio > 0.65) return false;
      const cx = x + rw / 2;
      const cy = y + rh / 2;
      const leftBound = 0.1 * w;
      const rightBound = 0.9 * w;
      const topBound = 0.1 * h;
      const bottomBound = 0.9 * h;
      if (
        cx < leftBound ||
        cx > rightBound ||
        cy < topBound ||
        cy > bottomBound
      )
        return false;
      const minW = 0.12 * w;
      const minH = 0.12 * h;
      if (rw < minW || rh < minH) return false;
      return true;
    },
    // 严格过滤规则：尺寸足够、位于中央区域、宽高比合理，避免背景/边缘误判（进一步收紧阈值）
    isValidFaceRect(rect, w, h) {
      if (!rect || !w || !h) return false;
      const x = Math.max(0, rect.x || rect.left || 0);
      const y = Math.max(0, rect.y || rect.top || 0);
      const rw = Math.max(0, rect.width || rect.w || 0);
      const rh = Math.max(0, rect.height || rect.h || 0);
      if (rw <= 0 || rh <= 0) return false;
      const ratio = rw / rh;
      // 更严格的宽高比范围
      if (ratio < 0.75 || ratio > 1.33) return false;
      // 面积比例限制，排除过小/过大区域
      const areaRatio = (rw * rh) / (w * h);
      if (areaRatio < 0.08 || areaRatio > 0.45) return false;
      // 中心区域更严格
      const cx = x + rw / 2;
      const cy = y + rh / 2;
      const leftBound = 0.22 * w;
      const rightBound = 0.78 * w;
      const topBound = 0.22 * h;
      const bottomBound = 0.78 * h;
      if (
        cx < leftBound ||
        cx > rightBound ||
        cy < topBound ||
        cy > bottomBound
      )
        return false;
      // 尺寸阈值
      const minW = 0.24 * w;
      const minH = 0.24 * h;
      if (rw < minW || rh < minH) return false;
      return true;
    },
    // 纹理方差过滤：排除低纹理（例如纯墙面、纯色背景）导致的误判
    rectHasTexture(ctx, rect) {
      try {
        const x = Math.max(0, Math.floor(rect.x || rect.left || 0));
        const y = Math.max(0, Math.floor(rect.y || rect.top || 0));
        const w = Math.max(1, Math.floor(rect.width || rect.w || 0));
        const h = Math.max(1, Math.floor(rect.height || rect.h || 0));
        const imgData = ctx.getImageData(x, y, w, h);
        const data = imgData.data;
        // 采样步长，控制计算量
        const step = Math.max(4, Math.floor(Math.min(w, h) / 20));
        let sum = 0,
          sumSq = 0,
          count = 0;
        for (let yy = 0; yy < h; yy += step) {
          for (let xx = 0; xx < w; xx += step) {
            const idx = (yy * w + xx) * 4;
            const r = data[idx],
              g = data[idx + 1],
              b = data[idx + 2];
            // 灰度
            const grey = r * 0.299 + g * 0.587 + b * 0.114;
            sum += grey;
            sumSq += grey * grey;
            count++;
          }
        }
        if (count <= 0) return false;
        const mean = sum / count;
        const variance = sumSq / count - mean * mean;
        const stddev = Math.sqrt(Math.max(0, variance));
        // 方差阈值，数值范围 0-255；低于该值认为纹理不足
        return stddev >= 18;
      } catch (e) {
        return false;
      }
    },
    postFace(file) {
      const formData = new FormData();
      formData.append("file", file);
      const devConfig =
        process.env.NODE_ENV === "development" ? { baseURL: "" } : {};
      this.$https
        .post(`/api/my/exam/faceVerify/${this.examinationId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          skipGlobalErrorToast: true,
          ...devConfig,
        })
        .then((res) => {
          if (res && (res.success === true || res.data === true)) {
            this.isVerifiedSuccess = true;
            this.$message.success("认证成功");
            this.$emit("success", res);
          } else {
            this.isVerifiedSuccess = false;
            this.mustRecapture = true;
            const msg =
              (res && (res.message || (res.data && res.data.message))) ||
              "认证失败，请重新拍照";
            this.$message.error(msg);
            this.$emit("error", res);
          }
        })
        .catch((err) => {
          this.isVerifiedSuccess = false;
          this.mustRecapture = true;
          const message =
            (err && err.data && (err.data.message || err.data.debug)) ||
            (err && err.message) ||
            "认证失败，请重新拍照";
          this.$message.error(message);
          this.$emit("error", err);
        });
    },
    dataURLtoFile(dataurl, filename) {
      const arr = dataurl.split(",");
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    },
    setTime() {
      const constraints = this.mediaConstraints || { video: true };
      navigator.mediaDevices
        .getUserMedia(constraints)
        .then((stream) => this.getMediaStreamSuccess(stream))
        .catch((err) => this.getMediaStreamError(err));
    },
    getMediaStreamSuccess(stream) {
      this.localStream = stream;
      const video = this.$refs.video;
      video.srcObject = stream;
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    },
    getMediaStreamError(error) {
      console.error(error);
      this.$message.error("无法打开摄像头，请检查权限或设备");
    },
    onStopTracking() {
      try {
        if (this.localStream) {
          this.localStream.getTracks().forEach((track) => track.stop());
          this.localStream = null;
        }
      } catch (e) {}
    },
    handleVerify() {
      if (
        !this.hasCaptured ||
        this.isTracking ||
        this.mustRecapture ||
        this.isDetecting
      )
        return;
      this.$message.info("正在提交认证，请稍候...");
      this.postFace(this.capturedFile);
    },
    // 将 Blob 转换为 DataURL（供检测与预览使用）
    blobToDataURL(blob) {
      return new Promise((resolve, reject) => {
        try {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = (err) => reject(err);
          reader.readAsDataURL(blob);
        } catch (e) {
          reject(e);
        }
      });
    },
    // 封装 Blob 为 File（上传接口需要 File 类型时使用）
    blobToFile(blob, filename) {
      try {
        return new File([blob], filename, { type: blob.type || "image/jpeg" });
      } catch (e) {
        return blob;
      }
    },
  },
};
</script>

<style scoped>
.videoContainer {
  width: 640px;
  height: 480px;
  border: 1px solid #ccc;
  margin: 20px auto;
  position: relative;
}
#video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
#canvas {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}
.videoOverlay {
  position: absolute;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 16px;
}
.title {
  text-align: center;
  font-size: 24px;
  color: #333;
}
.tipsText {
  text-align: center;
  font-size: 16px;
  color: #2f8f2f;
}
.inlineWarning {
  text-align: center;
  font-size: 16px;
  color: #d93025; /* 红色强调 */
  margin-top: 8px;
}
.buttons {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 12px;
}
</style>
